import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Shield, AlertCircle, ScrollText } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function ClientTerms() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const contentRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  // Dados passados da tela anterior (plano selecionado)
  const planData = location.state;

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }

    if (!planData) {
      // Se não tem dados do plano, verificar se veio do onboarding
      navigate('/client/plans');
      return;
    }

    fetchTerms();
  }, [user, navigate, planData]);

  const fetchTerms = async () => {
    try {
      const token = localStorage.getItem('token');
      // Usar o novo endpoint que suporta tipo
      const res = await axios.get(`${API}/admin/terms/active?type=client`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTerms(res.data);
    } catch (error) {
      // Fallback para o endpoint antigo se o novo falhar
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/terms/active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTerms(res.data);
      } catch (fallbackError) {
        toast.error('Erro ao carregar termos');
      }
    }
  };

  const handleScroll = () => {
    if (!contentRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 20;

    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    if (!hasScrolledToBottom) {
      toast.error('Por favor, leia todo o conteúdo até o final');
      return;
    }

    if (!checkboxChecked) {
      toast.error('Você deve marcar que leu e concordou com os termos');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/terms/accept`,
        { version: terms.version },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Termos aceitos com sucesso!');
      setHasAccepted(true);

      // Redirecionar para pagamento
      setTimeout(() => {
        navigate('/client/payment', { state: planData });
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aceitar termos');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = hasScrolledToBottom && checkboxChecked;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Card className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Termos de Uso</h1>
              <p className="text-sm text-muted-foreground">
                Versão {terms?.version} • JATOO
              </p>
            </div>
          </div>

          <Alert className="mb-6 bg-blue-50 border-blue-200">
            <ScrollText className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-sm text-blue-900">
              <strong>Para continuar, é necessário ler e aceitar integralmente os Termos de Uso.</strong>
              <br />
              Role até o final do documento e marque a caixa de confirmação.
            </AlertDescription>
          </Alert>

          {/* Conteúdo dos Termos com Scroll */}
          <div
            ref={contentRef}
            onScroll={handleScroll}
            className="border border-slate-200 rounded-lg p-6 mb-6 h-96 overflow-y-scroll bg-white"
            style={{ scrollBehavior: 'smooth' }}
          >
            {terms ? (
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                  {terms.content}
                </pre>
              </div>
            ) : (
              <p className="text-center text-muted-foreground">Carregando termos...</p>
            )}
          </div>

          {/* Indicador de Scroll */}
          {!hasScrolledToBottom && (
            <Alert className="mb-4 bg-amber-50 border-amber-200">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-sm text-amber-900">
                Você precisa rolar até o final do documento para continuar.
              </AlertDescription>
            </Alert>
          )}

          {/* Checkbox de Aceite */}
          <div className="flex items-start gap-3 mb-6 p-4 border border-slate-200 rounded-lg bg-slate-50">
            <Checkbox
              id="accept-terms"
              checked={checkboxChecked}
              onCheckedChange={setCheckboxChecked}
              disabled={!hasScrolledToBottom}
            />
            <label
              htmlFor="accept-terms"
              className={`text-sm leading-relaxed cursor-pointer ${
                !hasScrolledToBottom ? 'text-muted-foreground' : 'text-slate-900'
              }`}
            >
              Declaro que li e compreendi integralmente os Termos de Uso da JATOO.
              Concordo com todas as cláusulas e condições estabelecidas neste documento.
            </label>
          </div>

          {/* Botões */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/client/plans')}
              disabled={loading}
              className="flex-1"
            >
              Voltar
            </Button>
            <Button
              onClick={handleAccept}
              disabled={!canProceed || loading}
              className="flex-1"
            >
              {loading ? 'Processando...' : 'Aceitar e Continuar para Pagamento'}
            </Button>
          </div>

          {/* Informação Legal */}
          <p className="text-xs text-center text-muted-foreground mt-6">
            Seu aceite será registrado com data, hora e endereço IP para fins legais e de auditoria,
            conforme exigido pela legislação brasileira e LGPD.
          </p>
        </Card>
      </div>
    </div>
  );
}
